import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "../../../../layout/breadcrumb";
import { Button, Card, CardBody, Col, Container, Input, Row, Table } from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import SweetAlert from "sweetalert2";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import PreLoader from "../../../../share-components/ui/preLoader";
import {
    aiSuggestMappingsApi,
    getMetafieldValuesApi,
    getShopifyValuesApi,
    saveMetafieldMappingsApi,
    syncShopifyValuesApi,
    syncToShopifyApi,
    uploadEntryImageApi,
} from "../../../../services/manage-product/setting/categoryMetafieldMappingService";

const Spinner = () => <i className="fa fa-spinner fa-spin mr-1" aria-hidden="true" />;
const norm = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "");
const validHex = (h) => /^#[0-9a-fA-F]{6}$/.test(h || "");

const statusOptions = [
    { value: "all", label: "All values" },
    { value: "mapped", label: "Mapped only" },
    { value: "unmapped", label: "Unmapped only" },
];

const CategoryMetafieldMappingDetail = () => {
    const { metafieldKey } = useParams();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [pushing, setPushing] = useState(false);

    const [meta, setMeta] = useState(null);
    const [slots, setSlots] = useState([]);
    const [dataFields, setDataFields] = useState([]);
    const [rows, setRows] = useState([]);
    const [taxonomyCount, setTaxonomyCount] = useState(0);
    const [defaultBySlot, setDefaultBySlot] = useState({}); // { slotKey: [options] }
    const [entries, setEntries] = useState({});             // { our_key: { slotKey:{value,label}|null, color, image } }
    const [aiInfo, setAiInfo] = useState({});               // { our_key: { slotKey:{confidence,reason} } }
    const [uploadingKey, setUploadingKey] = useState(null);
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(statusOptions[0]);

    const dataMenu = [
        { type: 1, text: "Category Metafield Mapping", link: "/product/setting/category-metafield-mapping" },
        { type: 0, text: meta ? meta.label : metafieldKey },
    ];

    const loadData = async () => {
        // Guard against a transient undefined param during route transitions.
        if (!metafieldKey) return;
        setLoading(true);
        try {
            const { response } = await getMetafieldValuesApi(metafieldKey);
            const m = response.metafield || null;
            const sc = (m && m.schema) || { slots: [], data_fields: [] };
            setMeta(m);
            setSlots(sc.slots || []);
            setDataFields(sc.data_fields || []);
            setRows(response.rows || []);
            setTaxonomyCount(response.taxonomy_synced || 0);

            const hasColor = (sc.data_fields || []).some((d) => d.type === "color");
            const seed = {};
            (response.rows || []).forEach((r) => {
                const f = r.fields && typeof r.fields === "object" ? { ...r.fields } : {};
                if (hasColor && !f.color && r.default_hex) {
                    f.color = r.default_hex;
                }
                seed[r.our_key] = f;
            });
            setEntries(seed);
            setAiInfo({});

            // Starter options per slot (for the AsyncSelect defaultOptions).
            const starters = {};
            await Promise.all(
                (sc.slots || []).map(async (slot) => {
                    try {
                        const { response: sv } = await getShopifyValuesApi(metafieldKey, { slot: slot.key });
                        starters[slot.key] = sv.values || [];
                    } catch (e) {
                        starters[slot.key] = [];
                    }
                })
            );
            setDefaultBySlot(starters);
        } catch (e) {
            console.error("Metafield detail load failed", e);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (metafieldKey) loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metafieldKey]);

    const loadSlotOptions = (slotKey) => async (inputValue) => {
        try {
            const { response } = await getShopifyValuesApi(metafieldKey, {
                slot: slotKey,
                ...(inputValue ? { search: inputValue } : {}),
            });
            return response.values || [];
        } catch (e) {
            return [];
        }
    };

    const setSlotValue = (ourKey, slotKey, option) =>
        setEntries((prev) => ({ ...prev, [ourKey]: { ...(prev[ourKey] || {}), [slotKey]: option || null } }));

    const setDataValue = (ourKey, fieldKey, value) =>
        setEntries((prev) => ({ ...prev, [ourKey]: { ...(prev[ourKey] || {}), [fieldKey]: value } }));

    const primarySlot = slots[0] && slots[0].key;
    const isMapped = (ourKey) => !!(primarySlot && entries[ourKey] && entries[ourKey][primarySlot]);

    const filteredRows = useMemo(() => {
        const q = norm(search);
        const status = statusFilter?.value || "all";
        return rows.filter((r) => {
            if (q && !norm(r.our_value).includes(q)) return false;
            if (status === "mapped" && !isMapped(r.our_key)) return false;
            if (status === "unmapped" && isMapped(r.our_key)) return false;
            return true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, search, statusFilter, entries, primarySlot]);

    const mappedCount = useMemo(
        () => rows.filter((r) => isMapped(r.our_key)).length,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, entries, primarySlot]
    );

    const applySuggestions = (suggestions) => {
        setEntries((prev) => {
            const next = { ...prev };
            suggestions.forEach((s) => {
                if (s.shopify_value_gid) {
                    next[s.our_key] = { ...(next[s.our_key] || {}), [s.slot]: { value: s.shopify_value_gid, label: s.shopify_value_name } };
                }
            });
            return next;
        });
        setAiInfo((prev) => {
            const next = { ...prev };
            suggestions.forEach((s) => {
                next[s.our_key] = { ...(next[s.our_key] || {}), [s.slot]: { confidence: s.confidence, reason: s.reason } };
            });
            return next;
        });
        return suggestions.filter((s) => s.shopify_value_gid).length;
    };

    const handleSync = async () => {
        setSyncing(true);
        try {
            const { response } = await syncShopifyValuesApi(metafieldKey);
            toast.success(`Synced ${response.fetched} Shopify values.`);
            await loadData();
        } catch (e) {
            toast.error(e?.error || "Sync failed");
        }
        setSyncing(false);
    };

    const handleAiSuggest = async () => {
        setAiLoading(true);
        try {
            const { response } = await aiSuggestMappingsApi(metafieldKey);
            const filled = applySuggestions(response.suggestions || []);
            toast.success(`AI matched ${filled} bases — review, then Save.`);
            // Never let an incomplete AI reply pass silently.
            (response.warnings || []).forEach((w) => toast.warning(w));
        } catch (e) {
            toast.error(e?.error || "AI match failed (sync Shopify values first)");
        }
        setAiLoading(false);
    };

    const handleImageUpload = async (ourKey, file) => {
        if (!file) return;
        setUploadingKey(ourKey);
        try {
            const fd = new FormData();
            fd.append("image", file);
            const { response } = await uploadEntryImageApi(fd);
            if (response && response.url) {
                setDataValue(ourKey, "image", response.url);
                toast.success("Image uploaded.");
            } else {
                toast.error("Upload failed.");
            }
        } catch (e) {
            toast.error("Image upload failed");
        }
        setUploadingKey(null);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const mappings = rows.map((r) => ({
                our_key: r.our_key,
                our_value_name: r.our_value,
                fields: entries[r.our_key] || {},
            }));
            const { response } = await saveMetafieldMappingsApi(metafieldKey, { mappings });
            toast.success(`Saved ${response.saved} mappings.`);
        } catch (e) {
            toast.error("Failed to save mappings");
        }
        setSaving(false);
    };

    const handleSyncToShopify = async () => {
        setPushing(true);
        try {
            // Dry run first to preview counts, then confirm before writing.
            const { response: dry } = await syncToShopifyApi(metafieldKey, false);
            const html = `This will create/update <b>${dry.written}</b> entr${dry.written === 1 ? "y" : "ies"} in Shopify for "${meta?.label}".`
                + (dry.skipped ? `<br/><span class="text-muted">${dry.skipped} value(s) skipped — missing a required base.</span>` : "");
            const result = await SweetAlert.fire({
                title: "Sync to Shopify",
                html,
                icon: "warning",
                showCancelButton: true,
                animation: false,
                confirmButtonText: "Yes, sync",
                cancelButtonText: "Cancel",
                reverseButtons: true,
            });
            if (!result.isConfirmed) {
                setPushing(false);
                return;
            }
            const { response } = await syncToShopifyApi(metafieldKey, true);
            if (response.failed) {
                toast.error(`Synced ${response.written}; ${response.failed} failed. Please try again later.`);
                console.warn("Sync to Shopify errors:", response.errors);
            } else {
                toast.success(`${response.written} value${response.written === 1 ? "" : "s"} synced to Shopify successfully.`);
            }
        } catch (e) {
            toast.error(e?.error || "Sync to Shopify failed");
        }
        setPushing(false);
    };

    const confidenceColor = (c) => (c >= 80 ? "#28a745" : c >= 50 ? "#e0a800" : "#dc3545");
    const showTools = taxonomyCount > 0;
    const hasColorField = dataFields.some((f) => f.type === "color");
    const hasImageField = dataFields.some((f) => f.type === "image");
    const colSpan = 1 + slots.length + (hasColorField ? 1 : 0) + (hasImageField ? 1 : 0);
    // Keep base pickers compact when there are two (colour-pattern) so hex + image fit.
    const slotWidth = slots.length > 1 ? "26%" : "45%";

    const renderRow = (r) => {
        const entry = entries[r.our_key] || {};
        const ai = aiInfo[r.our_key] || {};
        return (
            <tr key={r.our_key}>
                <td style={{ verticalAlign: "middle" }}>{r.our_value}</td>
                {slots.map((slot) => (
                    <td key={slot.key} style={{ width: slotWidth, minWidth: 160 }}>
                        <AsyncSelect
                            isClearable
                            cacheOptions
                            defaultOptions={defaultBySlot[slot.key] || []}
                            loadOptions={loadSlotOptions(slot.key)}
                            placeholder={`Search ${slot.label}…`}
                            value={entry[slot.key] || null}
                            onChange={(opt) => setSlotValue(r.our_key, slot.key, opt)}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            noOptionsMessage={({ inputValue }) => (inputValue ? "No match" : "Type to search…")}
                        />
                        {ai[slot.key] && (ai[slot.key].confidence != null || ai[slot.key].reason) && (
                            <div className="mt-1" style={{ fontSize: 11 }}>
                                {ai[slot.key].confidence != null && (
                                    <span style={{ color: confidenceColor(ai[slot.key].confidence), fontWeight: 600 }}>
                                        AI {ai[slot.key].confidence}%
                                    </span>
                                )}
                                {ai[slot.key].reason && <span className="text-muted"> — {ai[slot.key].reason}</span>}
                            </div>
                        )}
                    </td>
                ))}
                {hasColorField && (
                    <td style={{ verticalAlign: "middle", width: 140 }}>
                        <div className="d-flex align-items-center">
                            <input
                                type="color"
                                value={validHex(entry.color) ? entry.color : "#000000"}
                                onChange={(e) => setDataValue(r.our_key, "color", e.target.value.toUpperCase())}
                                style={{ width: 34, height: 34, padding: 0, border: "1px solid #ced4da", borderRadius: 4, cursor: "pointer", flex: "0 0 auto" }}
                                title="Pick colour"
                            />
                            <input
                                type="text"
                                value={(entry.color || "").toUpperCase()}
                                onChange={(e) => setDataValue(r.our_key, "color", e.target.value.toUpperCase())}
                                placeholder="#RRGGBB"
                                maxLength={7}
                                className="form-control form-control-sm ml-2"
                                style={{ width: 90, textTransform: "uppercase" }}
                            />
                        </div>
                    </td>
                )}
                {hasImageField && (
                    <td style={{ verticalAlign: "middle", width: 110 }}>
                        {entry.image ? (
                            <div style={{ position: "relative", display: "inline-block" }}>
                                <img
                                    src={entry.image}
                                    alt=""
                                    title="Click to preview"
                                    onClick={() => setLightboxSrc(entry.image)}
                                    style={{ width: 46, height: 46, objectFit: "cover", borderRadius: 6, cursor: "pointer", border: "1px solid #e0e0e0", display: "block" }}
                                />
                                <button
                                    type="button"
                                    title="Remove image"
                                    onClick={() => setDataValue(r.our_key, "image", null)}
                                    style={{ position: "absolute", top: -8, right: -8, width: 20, height: 20, lineHeight: "16px", padding: 0, borderRadius: "50%", border: "2px solid #fff", background: "#dc3545", color: "#fff", fontSize: 13, cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }}
                                >
                                    ×
                                </button>
                            </div>
                        ) : (
                            <label className="btn btn-outline-primary btn-sm mb-0" style={{ cursor: "pointer" }}>
                                {uploadingKey === r.our_key ? <Spinner /> : <i className="fa fa-upload mr-1" />}
                                Upload
                                <input type="file" accept="image/*" hidden
                                    onChange={(e) => handleImageUpload(r.our_key, e.target.files && e.target.files[0])} />
                            </label>
                        )}
                    </td>
                )}
            </tr>
        );
    };

    return (
        <Fragment>
            <Container fluid={true} className="product-wrapper">
                <Breadcrumb data={dataMenu} title={meta ? `Map values — ${meta.label}` : "Map values"} />
                <div className="product-grid">
                    <div className="feature-products">
                        <Card>
                            <Row className="p-3 align-items-center">
                                <Col md="6">
                                    <h5 className="mb-1">{meta ? meta.label : metafieldKey}</h5>
                                    <small className="text-muted">
                                        Source: <b>{meta?.source_label || "—"}</b> · {mappedCount}/{rows.length} mapped ·
                                        Shopify values synced: <b>{taxonomyCount}</b>
                                        {taxonomyCount === 0 && " — click \"Sync Shopify Values\" first."}
                                    </small>
                                </Col>
                                <Col md="6">
                                    <div className="float-md-right mt-2 mt-md-0">
                                        <Button size="sm" color="secondary" outline className="mr-2" disabled={syncing} onClick={handleSync}>
                                            {syncing
                                                ? <Fragment><Spinner />Syncing&hellip;</Fragment>
                                                : taxonomyCount > 0 ? "Re-sync Shopify Values" : "Sync Shopify Values"}
                                        </Button>
                                        {showTools && (
                                            <Fragment>
                                                <Button size="sm" color="primary" outline className="mr-2" disabled={aiLoading} onClick={handleAiSuggest}>
                                                    {aiLoading ? <Fragment><Spinner />AI matching&hellip;</Fragment> : "Auto-match by AI"}
                                                </Button>
                                                <Button size="sm" color="primary" className="mr-2" disabled={saving} onClick={handleSave}>
                                                    {saving ? <Fragment><Spinner />Saving&hellip;</Fragment> : "Save Mappings"}
                                                </Button>
                                                <Button size="sm" color="success" disabled={pushing} onClick={handleSyncToShopify}>
                                                    {pushing ? <Fragment><Spinner />Syncing&hellip;</Fragment> : "Sync to Shopify"}
                                                </Button>
                                            </Fragment>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        <Card>
                            <CardBody>
                                <Row className="mb-1">
                                    <Col md="6" className="mb-2">
                                        <Input
                                            type="text"
                                            placeholder="Search our value…"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </Col>
                                    <Col md="4" className="mb-2">
                                        <Select
                                            options={statusOptions}
                                            value={statusFilter}
                                            onChange={(opt) => setStatusFilter(opt || statusOptions[0])}
                                        />
                                    </Col>
                                </Row>

                                {loading ? (
                                    <PreLoader />
                                ) : (
                                    <div className="table-responsive product-table mt-2">
                                        <Table className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: hasColorField ? "16%" : "34%" }}>Our Value (Label)</th>
                                                    {slots.map((slot) => (
                                                        <th key={slot.key} style={{ width: slotWidth }}>{slot.label}</th>
                                                    ))}
                                                    {hasColorField && <th style={{ width: 140 }}>Hex</th>}
                                                    {hasImageField && <th style={{ width: 110 }}>Image</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRows.map(renderRow)}
                                                {!filteredRows.length && (
                                                    <tr>
                                                        <td colSpan={colSpan} className="text-center text-muted py-4">
                                                            {rows.length ? "No values match your filters." : "No values found for this source."}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Container>
            {lightboxSrc && (
                <Lightbox mainSrc={lightboxSrc} onCloseRequest={() => setLightboxSrc(null)} />
            )}
        </Fragment>
    );
};

export default CategoryMetafieldMappingDetail;
